import React from 'react';
import { t } from '@lingui/core/macro';
import styled from 'styled-components';

import { Box } from '@rover/kibble/core';
import MQ from '@rover/kibble/styles/MediaQuery';
import Advanced from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/Advanced';
import PriceFilter from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/PriceFilter';
import SpacesRequired from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/SpacesRequired';
import StarSitterFilter from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/StarSitterFilter';
import {
  advancedFiltersCounter,
  hasAdvancedFilters,
  shouldShowControls,
} from '@rover/react-lib/src/pages/search/SearchPage/utilities';
import { usePetInfoCalloutsExperiment } from '@rover/react-lib/src/pages/search/SearchPage/utilities/usePetInfoCalloutsExperiment';
import type { ApiFrontendConfigurationRetrieve200SettingsDogSizeBuckets } from '@rover/rsdk/src/apiClient/latest/model/apiFrontendConfigurationRetrieve200SettingsDogSizeBuckets';
import useBrandSpecificPets from '@rover/shared/js/hooks/useBrandSpecificPets';
import useCurrentUserShim from '@rover/shared/js/hooks/useCurrentUserShim';
import useFlag, { FLAGS } from '@rover/shared/js/hooks/useFlag';
import PetSize from '@rover/shared/js/search/components/PetSize';
import getIsCatOnlyPetType from '@rover/shared/js/search/utilities/getIsCatOnlyPetType';
import type { SearchDerivedData } from '@rover/types/src/apiResponses/FetchSearchResponse';
import type { SearchFiltersOptions } from '@rover/types/src/apiResponses/SearchOptionsResponse';
import type { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';
import { SearchCardTreatment } from '@rover/types/src/SearchCardTreatment';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import { ServiceTypeSlugs } from '@rover/types/src/Service';
import type { ServiceType } from '@rover/types/src/ServiceType';
import { isCatOnlyAvailablePetTypesBasedOnPetTypes } from '@rover/utilities/getAvailablePetTypes/availablePetTypesUtils';
import { getIsPriceTransparency } from '@rover/utilities/priceTransparency';

import { FilterWrapper } from '../common';
import { CatPreferences, DogPreferences } from '../Preferences';
import PuppyPreferences from '../Preferences/PuppyPreferences';

export type Props = {
  getServiceTypeBySlug: (arg0: string) => ServiceType | null | undefined;
  currencyCode: string;
  formData: Partial<SearchFilters>;
  getPriceUnit: (arg0: string) => string;
  hasPets: boolean;
  language: string;
  onChange: (arg0: Partial<SearchFilters>) => void;
  petSizeBuckets: ApiFrontendConfigurationRetrieve200SettingsDogSizeBuckets;
  searchFilters: SearchFilters;
  serviceTypeFilterOptions: SearchFiltersOptions;
  weightUnit: string;
  priceFilterMaxValue: number;
  priceFilterMinValue: number;
  queryDerivedData: SearchDerivedData;
  priceTransparencyLevel: PriceTransparencyLevel;
  cardTreatment: SearchCardTreatment;
};

const AdvancedWrapper = styled(FilterWrapper)`
  margin-left: ${({ theme }) => theme.space['-4x']};
  margin-right: ${({ theme }) => theme.space['-4x']};
  margin-bottom: 0;
  padding: ${({ theme }) => theme.space['4x']};
  padding-bottom: ${({ theme }) => theme.space['16x']};
  background-color: ${({ theme }) => theme.background.secondary};

  ${MQ.XS_DOWN.toString()} {
    margin-bottom: 0;
  }
`;

const AdvancedFiltersCollectionV2 = ({
  currencyCode,
  formData,
  getPriceUnit,
  hasPets,
  language,
  onChange,
  petSizeBuckets,
  searchFilters,
  serviceTypeFilterOptions,
  weightUnit,
  priceFilterMaxValue,
  priceFilterMinValue,
  getServiceTypeBySlug,
  priceTransparencyLevel,
  queryDerivedData,
  cardTreatment,
}: Props): JSX.Element => {
  const brandAvailablePets = useBrandSpecificPets();
  const isCatOnlyBrand = isCatOnlyAvailablePetTypesBasedOnPetTypes(brandAvailablePets);
  const serviceType = getServiceTypeBySlug(searchFilters.serviceType);
  const { starSitterLink } = queryDerivedData;
  const { isStaffOrImpersonating } = useCurrentUserShim();
  const { isActive: inPetInfoCalloutsExperiment } = usePetInfoCalloutsExperiment();
  const isFiltersM2 = useFlag(FLAGS.ROLLOUT_MOBILE_FILTERS_M2);

  const { showCatPreferences, showDogPreferences, showPetSize, showPrice, showPuppyPreferences } =
    shouldShowControls({
      hasPets,
      isCatOnly: getIsCatOnlyPetType(formData.petType || []) || isCatOnlyBrand,
      serviceOptions: serviceTypeFilterOptions,
      serviceTypeSlug: searchFilters.serviceType,
      isStaffOrImpersonating,
      searchFilters,
      inPetInfoCalloutsExperiment,
    });

  /* The spaces_required query parameter depends on how many walks/visits OR how many pets you have. For the new design
   * we will handle number of pets separately but still need to check number of walks and visits. */
  const showSpacesRequired =
    serviceType &&
    (serviceType.slug === ServiceTypeSlugs.dogWalking ||
      serviceType.slug === ServiceTypeSlugs.dropIn);

  return (
    <>
      {showPetSize && (
        <FilterWrapper>
          <PetSize
            label={t`What size are your dogs (${weightUnit})?`}
            onChange={(sizeKey) => {
              onChange({ [sizeKey]: !formData[sizeKey] });
            }}
            petSizeBuckets={petSizeBuckets}
            searchFilters={formData}
            weightUnit={weightUnit}
          />
        </FilterWrapper>
      )}
      {showSpacesRequired && (
        <FilterWrapper>
          <SpacesRequired
            searchFilters={formData}
            onChange={(spacesRequired) => onChange({ spacesRequired })}
          />
        </FilterWrapper>
      )}
      {showPuppyPreferences && (
        <FilterWrapper>
          <PuppyPreferences
            displayAsToggle
            onChange={(puppy) => onChange({ puppy })}
            value={formData.puppy}
          />
        </FilterWrapper>
      )}
      {showCatPreferences && (
        <FilterWrapper>
          <CatPreferences
            displayAsToggle
            onChange={(noCats) => onChange({ noCats })}
            value={formData.noCats}
          />
        </FilterWrapper>
      )}
      {showDogPreferences && (
        <FilterWrapper>
          <DogPreferences
            displayAsToggle
            onChange={(personDoesNotHaveDogs) => onChange({ personDoesNotHaveDogs })}
            value={formData.personDoesNotHaveDogs}
          />
        </FilterWrapper>
      )}
      {!!(showPrice && formData.serviceType && formData.minprice) && (
        <FilterWrapper>
          <PriceFilter
            currencyCode={currencyCode}
            priceUnit={getPriceUnit(formData.serviceType)}
            priceTransparencyLevel={priceTransparencyLevel}
            isStaffOrImpersonating={isStaffOrImpersonating}
            language={language}
            onChange={({ min, max }) => {
              onChange({ minprice: min, maxprice: max });
            }}
            selectedRangeMin={formData.minprice}
            selectedRangeMax={formData.maxprice || priceFilterMaxValue}
            sliderMax={priceFilterMaxValue}
            sliderMin={priceFilterMinValue}
            showPriceInputs={isFiltersM2}
          />
        </FilterWrapper>
      )}
      <Box>
        <FilterWrapper>
          <StarSitterFilter
            label={t`Only show star sitters`}
            onChange={(starSitter) => onChange({ starSitter })}
            value={formData.starSitter || false}
            starSitterLink={starSitterLink}
          />
        </FilterWrapper>
      </Box>
      {hasAdvancedFilters(serviceTypeFilterOptions) && (
        <AdvancedWrapper>
          <Advanced
            advancedFiltersCount={advancedFiltersCounter(formData, serviceTypeFilterOptions)}
            onChange={(filter) => onChange(filter)}
            searchFiltersOptions={serviceTypeFilterOptions}
            searchFilters={formData}
            isPriceTransparency={getIsPriceTransparency(priceTransparencyLevel)}
            cardTreatment={cardTreatment}
          />
        </AdvancedWrapper>
      )}
    </>
  );
};

export default AdvancedFiltersCollectionV2;
